import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CardItem, { CardItemVariant } from './card-item';
import DateAndTimeInfo from './date-and-time-info';

export interface CalendarColumn {
  id: string;
  label: string;
  items: {
    id: string;
    time: string;
    title?: string;
    cardType?: CardItemVariant;
    rawData?: any; // API'den gelen orijinal veri
  }[];
  globalIndex?: number; // Mavi slotu bulmak için
  isDifferentDate?: boolean; // Seçili tarihten farklı tarih mi?
  slotDateTime?: string; // Slotun tam tarih-saat bilgisi (ISO)
}

interface CalendarBoardProps {
  columns: CalendarColumn[];
  onPrev?: () => void;
  onNext?: () => void;
  highlightFromIndex?: number;
  patientId?: string | number;
  upn?: string | number;
  refetch?: () => void;
}

const CalendarBoard: React.FC<CalendarBoardProps> = ({
  columns,
  onPrev,
  onNext,
  highlightFromIndex,
  patientId,
  upn,
  refetch,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const columnRefs = useRef<HTMLDivElement[]>([]);
  const hasScrolledToHighlight = useRef(false);

  // Title swipe için state'ler
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const swipeStartX = useRef(0);
  const swipeStartScrollLeft = useRef(0);
  const swipeThreshold = 50; // Minimum kaydırma mesafesi (px)

  // Sadece ilk render'da mavi slotu ortala, sonra scroll'a karışma
  useEffect(() => {
    if (
      hasScrolledToHighlight.current ||
      typeof highlightFromIndex !== 'number' ||
      highlightFromIndex < 0 ||
      !containerRef.current
    ) {
      return;
    }

    // Kolonların render olmasını bekle
    setTimeout(() => {
      const target = columnRefs.current[highlightFromIndex];
      if (!target || !containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const targetLeft = target.offsetLeft;
      const targetWidth = target.offsetWidth;

      // Mavi slotu ortaya al: target'ın ortası container'ın ortasına gelsin
      const scrollLeft = targetLeft - containerWidth / 2 + targetWidth / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });

      hasScrolledToHighlight.current = true;
    }, 100);
  }, [highlightFromIndex]);

  // Title'larda swipe başlatma
  const handleTitleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    setIsSwipeActive(true);
    swipeStartX.current = e.pageX;
    swipeStartScrollLeft.current = containerRef.current.scrollLeft;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
  }, []);

  const handleTitleTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    setIsSwipeActive(true);
    swipeStartX.current = e.touches[0].pageX;
    swipeStartScrollLeft.current = containerRef.current.scrollLeft;
  }, []);

  // Titles alanında swipe bitince navigation'ı tetikle
  const handleSwipeEnd = useCallback((diff: number) => {
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0 && onNext) {
        onNext();
      } else if (diff < 0 && onPrev) {
        onPrev();
      }
    }
  }, [onNext, onPrev]);

  // Global mouse/touch move handler
  useEffect(() => {
    if (!isSwipeActive) return;

    const handleGlobalMove = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      // Görsel geri bildirim için scrollLeft'i geçici olarak değiştiriyoruz ama asıl değişim onNext/onPrev ile olacak
      const currentX = 'touches' in e ? e.touches[0].pageX : e.pageX;
      const walk = (swipeStartX.current - currentX);
      containerRef.current.scrollLeft = swipeStartScrollLeft.current + walk;
    };

    const handleGlobalEnd = (e: MouseEvent | TouchEvent) => {
      const currentX = 'touches' in e ? (e.changedTouches?.[0]?.pageX || swipeStartX.current) : (e as MouseEvent).pageX;
      const diff = swipeStartX.current - currentX;

      handleSwipeEnd(diff);

      setIsSwipeActive(false);
      document.body.style.userSelect = 'auto';
      document.body.style.cursor = 'auto';
    };

    document.addEventListener('mousemove', handleGlobalMove);
    document.addEventListener('mouseup', handleGlobalEnd);
    document.addEventListener('touchmove', handleGlobalMove, { passive: false });
    document.addEventListener('touchend', handleGlobalEnd);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMove);
      document.removeEventListener('mouseup', handleGlobalEnd);
      document.removeEventListener('touchmove', handleGlobalMove);
      document.removeEventListener('touchend', handleGlobalEnd);
      document.body.style.userSelect = 'auto';
      document.body.style.cursor = 'auto';
    };
  }, [isSwipeActive, handleSwipeEnd]);

  const columnCount = columns.length;

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E0E5EB',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.08)',
        p: { xs: 0.4, sm: 0.6, md: 0.8 },
      }}
    >
      {/* Mobil için üstte oklar */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
          px: 1,
        }}
      >
        <IconButton
          size="small"
          onClick={onPrev}
          sx={{
            borderRadius: '50%',
            border: '1px solid transparent',
            '&:hover': { bgcolor: '#2979C3', borderColor: '#2979C3' },
            '&:hover .MuiSvgIcon-root': { color: '#fff' },
          }}
        >
          <NavigateBeforeIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={onNext}
          sx={{
            borderRadius: '50%',
            border: '1px solid transparent',
            '&:hover': { bgcolor: '#2979C3', borderColor: '#2979C3' },
            '&:hover .MuiSvgIcon-root': { color: '#fff' },
          }}
        >
          <NavigateNextIcon />
        </IconButton>
      </Box>

      {/* Grid: Desktop'ta sol buton + Kolonlar + sağ buton */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: `repeat(${columnCount}, 1fr)`,
            md: `auto repeat(${columnCount}, 1fr) auto`,
          },
          gap: { xs: 0.25, sm: 0.35, md: 0.4 },
          width: '100%',
          alignItems: 'flex-start',
        }}
        ref={containerRef}
      >
        {/* Desktop için sol buton */}
        <IconButton
          size="small"
          onClick={onPrev}
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifySelf: 'start',
            alignSelf: 'flex-start',
            mt: 0.5,
            minWidth: '32px',
            width: '32px',
            height: '32px',
            padding: 0,
            borderRadius: '50%',
            border: '1px solid transparent',
            '&:hover': {
              bgcolor: '#2979C3',
              borderColor: '#2979C3',
            },
            '&:hover .MuiSvgIcon-root': {
              color: '#fff',
            },
          }}
        >
          <NavigateBeforeIcon fontSize="small" />
        </IconButton>

        {/* Kolonlar */}
        {columns.map((col, idx) => (
          <Box
            key={col.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
            ref={(el: HTMLDivElement | null) => {
              if (el) columnRefs.current[idx] = el;
            }}
          >
            {/* Title - Swipe desteği */}
            <Box
              sx={{
                width: '100%',
                mb: 0.75,
                cursor: isSwipeActive ? 'grabbing' : 'grab',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
              onMouseDown={handleTitleMouseDown}
              onTouchStart={handleTitleTouchStart}
            >
              <DateAndTimeInfo
                label={col.label}
                highlighted={idx === highlightFromIndex}
                isDifferentDate={col.isDifferentDate}
              />
            </Box>

            {/* Kartlar */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
              {col.items.map((item, itemIdx) => (
                <Box key={item.id || `${col.id}-item-${itemIdx}`} sx={{ width: '100%' }}>
                  <CardItem type={item.cardType} data={item.rawData} patientId={patientId} upn={upn} refetch={refetch} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}

        {/* Desktop için sağ buton */}
        <IconButton
          size="small"
          onClick={onNext}
          sx={{
            display: { xs: 'none', md: 'flex' },
            justifySelf: 'end',
            alignSelf: 'flex-start',
            mt: 0.5,
            minWidth: '32px',
            width: '32px',
            height: '32px',
            padding: 0,
            borderRadius: '50%',
            border: '1px solid transparent',
            '&:hover': {
              bgcolor: '#2979C3',
              borderColor: '#2979C3',
            },
            '&:hover .MuiSvgIcon-root': {
              color: '#fff',
            },
          }}
        >
          <NavigateNextIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CalendarBoard;


